import { useState } from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useNavigate } from 'react-router-dom';

import { ErroDaApi } from '../../shared/api/erros';
import { ConfirmacaoHumana } from '../../shared/ui/ConfirmacaoHumana';
import { aplicarTransicao, definirModalidade } from './api/escritas';
import {
  EFEITOS_DE_GATILHO,
  MODALIDADES_DO_CASO,
  ROTULOS_DE_GATILHO,
  ROTULOS_DE_MODALIDADE,
  ehModalidadeDoCaso,
  type CasoDetalhado,
  type GatilhoDoCaso,
  type ModalidadeDoCaso,
} from './api/tipos';

/**
 * Mensagem de cada desfecho de escrita: nenhum vira sucesso silencioso.
 * `null` = a sessão expirou e a pessoa já foi levada à tela de entrada.
 *
 * **A MENSAGEM DO SERVIDOR VEM PRIMEIRO.** Ela nasce no `ErrorCatalog`, foi
 * escrita para a pessoa e NOMEIA o que falta — "ainda falta fechar: cadastro",
 * "o passo da vez é o 2". Mapear só por `status` descartava tudo isso: todo
 * 409 virava "o caso mudou enquanto você agia", que é o texto de UM caso
 * (`CASE_CONCURRENT_UPDATE`) servido a todos. A porta 1 da captação, que
 * também é 409, chegava à tela dizendo a coisa errada.
 *
 * O `status` continua decidindo o que a TELA faz — sair para o login, pedir
 * recarga —, e os textos abaixo ficam como rede para resposta sem `codigo`.
 */
function mensagemDeErro(causa: unknown, aoExpirar: () => void): string | null {
  if (causa instanceof ErroDaApi) {
    if (causa.status === 401) {
      aoExpirar();
      return null;
    }
    if (causa.codigo !== null && causa.message !== '') return causa.message;
    if (causa.status === 404) return 'O caso não foi encontrado para o seu núcleo.';
    if (causa.status === 409) return 'O caso mudou enquanto você agia e nada foi gravado. Carregamos o estado atual.';
    if (causa.status === 422) return 'Caso encerrado: não aceita mais alterações.';
    if (causa.status === 400) return 'A API recusou esta combinação. Confira os campos.';
  }
  return 'Não foi possível concluir a ação. Tente novamente.';
}

/**
 * Botões dos gatilhos que a API diz que ainda valem (o front NÃO copia a máquina de estados). Cada clique abre a
 * confirmação humana, que diz o que será registrado. Nada sai do sistema: todo gatilho só REGISTRA a transição.
 */
export function TransicoesDoCaso({
  caso,
  aoAtualizar,
  aoRecarregar,
  reiniciarAvisoQuando,
}: {
  caso: CasoDetalhado;
  aoAtualizar: (caso: CasoDetalhado) => void;
  aoRecarregar: () => void;
  /** Quando o valor muda, o aviso de uma tentativa barrada some: a causa dele (ex.: a alçada sem registro) foi resolvida. */
  reiniciarAvisoQuando?: number;
}): React.JSX.Element | null {
  const navegar = useNavigate();
  const [confirmando, definirConfirmando] = useState<GatilhoDoCaso | null>(null);
  const [enviando, definirEnviando] = useState(false);
  // O aviso guarda o contador em que nasceu: derivado, e não zerado num efeito. Mudou o contador, o aviso não vale mais.
  const [aviso, definirAviso] = useState<{ texto: string; nascidoEm: number | undefined } | null>(null);
  const erro = aviso !== null && aviso.nascidoEm === reiniciarAvisoQuando ? aviso.texto : null;

  if (caso.gatilhosDisponiveis.length === 0) {
    return null;
  }

  const aplicar = (gatilho: GatilhoDoCaso): void => {
    definirEnviando(true);
    definirAviso(null);
    aplicarTransicao(caso.id, gatilho)
      .then((atualizado) => {
        definirConfirmando(null);
        aoAtualizar(atualizado);
      })
      .catch((causa: unknown) => {
        definirConfirmando(null);
        const texto = mensagemDeErro(causa, () => void navegar('/entrar', { replace: true }));
        definirAviso(texto === null ? null : { texto, nascidoEm: reiniciarAvisoQuando });
        if (causa instanceof ErroDaApi && causa.status === 409) aoRecarregar();
      })
      .finally(() => {
        definirEnviando(false);
      });
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {caso.gatilhosDisponiveis.map((gatilho) => (
          <Button
            key={gatilho}
            variant="outlined"
            disabled={enviando}
            onClick={() => {
              definirConfirmando(gatilho);
            }}
          >
            {ROTULOS_DE_GATILHO[gatilho]}
          </Button>
        ))}
      </div>

      {erro !== null ? (
        <p role="alert" className="text-[0.8125rem] text-[var(--roxo)]">
          {erro}
        </p>
      ) : null}

      <ConfirmacaoHumana
        aberto={confirmando !== null}
        titulo={confirmando === null ? '' : `${ROTULOS_DE_GATILHO[confirmando]}?`}
        descricao={confirmando === null ? '' : EFEITOS_DE_GATILHO[confirmando]}
        rotuloDeConfirmar={confirmando === null ? 'Confirmar' : ROTULOS_DE_GATILHO[confirmando]}
        ocupado={enviando}
        aoCancelar={() => {
          definirConfirmando(null);
        }}
        aoConfirmar={() => {
          if (confirmando !== null) aplicar(confirmando);
        }}
      />
    </div>
  );
}

/**
 * Define ou corrige a modalidade (`AD` ou `ID`, e a ventilação contínua quando `ID`). Corrigir depois da autorização
 * recalcula o prazo de implantação, e a tela avisa. Qualquer pessoa do núcleo pode; o evento guarda quem e o valor anterior.
 */
export function ModalidadeDoCasoForm({
  caso,
  aoAtualizar,
  aoRecarregar,
}: {
  caso: CasoDetalhado;
  aoAtualizar: (caso: CasoDetalhado) => void;
  aoRecarregar: () => void;
}): React.JSX.Element {
  const navegar = useNavigate();
  const [modalidade, definirModalidadeEscolhida] = useState<ModalidadeDoCaso | ''>(caso.modalidade ?? '');
  const [ventilacao, definirVentilacao] = useState(caso.ventilacaoContinua);
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  const encerrado = caso.estado === 'Closed';
  const mudou = modalidade !== (caso.modalidade ?? '') || (modalidade === 'ID' && ventilacao !== caso.ventilacaoContinua);
  const autorizado = caso.autorizadoEm !== null;

  const salvar = (): void => {
    if (!ehModalidadeDoCaso(modalidade)) return;
    definirEnviando(true);
    definirErro(null);
    // Ventilação só vale com ID: fora dele a tela manda `false`, nunca o valor que sobrou.
    definirModalidade(caso.id, modalidade, modalidade === 'ID' && ventilacao)
      .then(aoAtualizar)
      .catch((causa: unknown) => {
        definirErro(mensagemDeErro(causa, () => void navegar('/entrar', { replace: true })));
        if (causa instanceof ErroDaApi && causa.status === 409) aoRecarregar();
      })
      .finally(() => {
        definirEnviando(false);
      });
  };

  if (encerrado) {
    return (
      <p className="text-[0.8125rem] text-[var(--tinta-2)]">
        Caso encerrado: a modalidade{' '}
        {caso.modalidade === null ? 'não foi definida' : ROTULOS_DE_MODALIDADE[caso.modalidade]} não muda mais.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <TextField
          select
          size="small"
          label="Modalidade"
          value={modalidade}
          disabled={enviando}
          onChange={(evento) => {
            const escolhida = evento.target.value;
            definirModalidadeEscolhida(ehModalidadeDoCaso(escolhida) ? escolhida : '');
            if (escolhida !== 'ID') definirVentilacao(false);
          }}
          sx={{ minWidth: 260 }}
        >
          {caso.modalidade === null ? (
            <MenuItem value="" disabled>
              Não definida
            </MenuItem>
          ) : null}
          {MODALIDADES_DO_CASO.map((codigo) => (
            <MenuItem key={codigo} value={codigo}>
              {ROTULOS_DE_MODALIDADE[codigo]}
            </MenuItem>
          ))}
        </TextField>

        {modalidade === 'ID' ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={ventilacao}
                disabled={enviando}
                onChange={(evento) => {
                  definirVentilacao(evento.target.checked);
                }}
              />
            }
            label="Ventilação contínua"
          />
        ) : null}

        <Button variant="outlined" disabled={enviando || !mudou || modalidade === ''} onClick={salvar}>
          Salvar modalidade
        </Button>
      </div>

      {autorizado && mudou ? (
        <p className="text-[0.75rem] text-[var(--tinta-2)]">
          O caso já foi autorizado: salvar recalcula o prazo de implantação a partir da autorização, pelas horas da nova
          modalidade.
        </p>
      ) : null}

      {erro !== null ? (
        <p role="alert" className="text-[0.8125rem] text-[var(--roxo)]">
          {erro}
        </p>
      ) : null}
    </div>
  );
}
